import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, Minus, Plus, Search, Workflow, X } from "lucide-react";
import { useAutomations } from "@/features/automations/hooks/useAutomations";
import type AutomationSummary from "@/features/automations/models/AutomationSummary";

export interface AssignWorkflowModalProps {
  workspaceId: string;
  workspaceName: string;
  /** Ids ya archivados acá, para pintar el estado sin re-consultar el detalle. */
  assignedWorkflowIds: string[];
  onAssign: (workflowId: string) => Promise<void>;
  onRemove: (workflowId: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Archiva automatizaciones en el workspace. Solo aparecen las **propias**: el backend rechaza
 * archivar la de otro, porque hacerlo la sacaría del workspace de esa persona.
 *
 * Una automatización pertenece a un solo workspace, así que archivar una que ya está en otro la
 * **mueve**; el modal lo avisa en la fila en vez de fallar al guardar.
 */
export default function AssignWorkflowModal({
  workspaceId,
  workspaceName,
  assignedWorkflowIds,
  onAssign,
  onRemove,
  onClose,
}: AssignWorkflowModalProps) {
  const { loading, getAll } = useAutomations();

  const [automations, setAutomations] = useState<AutomationSummary[]>([]);
  const [assigned, setAssigned] = useState<Set<string>>(new Set(assignedWorkflowIds));
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<Set<string>>(new Set());

  useEffect(() => {
    let ignore = false;
    void (async () => {
      const result = await getAll();
      if (!ignore && result) setAutomations(result.filter((a) => a.isOwner));
    })();
    return () => {
      ignore = true;
    };
  }, [getAll]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? automations.filter((a) => a.name.toLowerCase().includes(q)) : automations;
    // Las ya archivadas acá arriba: el modal se usa tanto para sumar como para sacar.
    return [...list].sort(
      (a, b) => Number(assigned.has(b.workflowId)) - Number(assigned.has(a.workflowId)),
    );
  }, [automations, assigned, query]);

  const withPending = async (workflowId: string, action: () => Promise<void>) => {
    setPending((prev) => new Set(prev).add(workflowId));
    try {
      await action();
    } catch {
      // el toast de error lo dispara useAsync
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(workflowId);
        return next;
      });
    }
  };

  const handleToggle = (automation: AutomationSummary) => {
    const { workflowId } = automation;
    if (pending.has(workflowId)) return;

    if (assigned.has(workflowId)) {
      void withPending(workflowId, async () => {
        await onRemove(workflowId);
        setAssigned((prev) => {
          const next = new Set(prev);
          next.delete(workflowId);
          return next;
        });
      });
      return;
    }

    void withPending(workflowId, async () => {
      await onAssign(workflowId);
      setAssigned((prev) => new Set(prev).add(workflowId));
      // El backend la movió: el chip de su workspace anterior ya no aplica.
      setAutomations((prev) =>
        prev.map((a) =>
          a.workflowId === workflowId
            ? { ...a, workspaceId, workspaceName }
            : a,
        ),
      );
    });
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/40 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="relative z-10 flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-zinc-200/50 bg-white/90 shadow-2xl backdrop-blur-2xl"
        >
          <div className="flex items-start gap-4 border-b border-black/5 p-6">
            <div className="shrink-0 rounded-2xl bg-accent/10 p-3">
              <Workflow className="size-6 text-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-lg font-bold text-foreground">
                Automatizaciones de «{workspaceName}»
              </h3>
              <p className="mt-0.5 text-sm text-foreground/50">
                Solo las tuyas. Una automatización vive en un solo workspace: archivar una que ya
                está en otro la mueve.
              </p>
            </div>
            <button
              onClick={onClose}
              title="Cerrar"
              className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-black/5 bg-white/50 text-foreground/40 transition-all hover:bg-white hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="border-b border-black/5 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/30" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar automatización…"
                className="w-full rounded-xl border border-black/5 bg-white/80 py-2.5 pl-10 pr-4 text-sm text-foreground shadow-sm transition-all placeholder:text-foreground/30 focus:border-accent/30 focus:outline-none focus:ring-4 focus:ring-accent/5"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {loading && automations.length === 0 ? (
              <div className="flex justify-center py-10">
                <Loader2 className="size-5 animate-spin text-foreground/30" />
              </div>
            ) : visible.length === 0 ? (
              <p className="py-10 text-center text-sm text-foreground/40">
                No hay automatizaciones propias que coincidan.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {visible.map((automation) => {
                  const isAssigned = assigned.has(automation.workflowId);
                  const isPending = pending.has(automation.workflowId);
                  const elsewhere =
                    !isAssigned &&
                    automation.workspaceId != null &&
                    automation.workspaceId !== workspaceId;

                  return (
                    <li
                      key={automation.workflowId}
                      className="flex items-center gap-3 rounded-xl border border-black/5 bg-white/60 px-3 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground/90">
                          {automation.name}
                        </p>
                        <p className="truncate text-[11px] text-foreground/40">
                          {isAssigned
                            ? "Archivada en este workspace"
                            : elsewhere
                              ? `Hoy en «${automation.workspaceName}» — archivarla acá la mueve`
                              : "Sin agrupar"}
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggle(automation)}
                        disabled={isPending}
                        title={isAssigned ? "Quitar del workspace" : "Archivar acá"}
                        className={`flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-xl border transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                          isAssigned
                            ? "border-accent/20 bg-accent/10 text-accent hover:border-rose-500 hover:bg-rose-500 hover:text-white"
                            : "border-black/5 bg-white/60 text-foreground/50 hover:border-accent hover:bg-accent hover:text-white"
                        }`}
                      >
                        {isPending ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : isAssigned ? (
                          <span className="group/btn">
                            <Check className="size-3.5 group-hover/btn:hidden" />
                            <Minus className="hidden size-3.5 group-hover/btn:block" />
                          </span>
                        ) : (
                          <Plus className="size-3.5" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
